import {area, booleanPointInPolygon, centroid, concave, convex, featureCollection, point} from '@turf/turf';
import {CIRCUIT_COULEUR} from '../tokens.js';
import {normaliser} from './recherche.js';

// Lot 7 : circuits (= patrimoines). Couleur stable, liste pour le sélecteur, enveloppes pour le calque carte.

/**
 * C1 : couleur d'un circuit d'après son RANG parmi les patrimoines triés par id (calculée dans `indexer`, lue ensuite
 * dans `patrimoine.couleur`). Teinte = rang × angle d'or mod 360 ; luminosité [36, 44, 52, 60] % selon rang mod 4. `clair` = fonds.
 */
export function couleurRang(rang, {clair = false} = {}) {
    const teinte = (rang * CIRCUIT_COULEUR.angle) % 360;
    const l = clair ? CIRCUIT_COULEUR.luminositeClair : CIRCUIT_COULEUR.luminosites[rang % 4];
    return `hsl(${Math.round(teinte * 10) / 10}, ${CIRCUIT_COULEUR.saturation}%, ${l}%)`;
}

const ordreGroupe = {intra: 0, extra: 1};
const cacheListe = new WeakMap();

/**
 * C2 : circuits triés (groupe intra puis extra, tri naturel du libellé).
 * `nbPav` = PAV actifs ; `retire` = aucun PAV actif (circuit « retiré »).
 */
export function listeCircuits(index) {
    let liste = cacheListe.get(index);
    if (!liste) {
        const actifs = new Map();
        for (const pav of index.pavs) {
            if (pav.statut !== 'RETIRÉ') actifs.set(pav.patrimoineId, (actifs.get(pav.patrimoineId) || 0) + 1);
        }
        liste = [...index.patrimoineById.values()]
            .map((p) => ({id: p.id, libelle: p.libelle, groupe: p.groupe, couleur: p.couleur, couleurClair: p.couleurClair, nbPav: actifs.get(p.id) || 0, retire: !actifs.has(p.id), norme: normaliser(p.libelle)}))
            .sort((a, b) => (ordreGroupe[a.groupe] ?? 2) - (ordreGroupe[b.groupe] ?? 2)
                || String(a.libelle).localeCompare(String(b.libelle), 'fr', {numeric: true, sensitivity: 'base'}));
        cacheListe.set(index, liste);
    }
    return liste;
}

/** Circuits dont le libellé contient la saisie (insensible à la casse et aux accents). */
export function filtrerCircuits(options, saisie) {
    const q = normaliser(saisie);
    return q === '' ? options : options.filter((o) => o.norme.includes(q));
}

/** Libellé du polygone : libellé complet, tronqué à 28 caractères avec « … ». */
export function libelleCourt(libelle) {
    const brut = String(libelle || '').trim();
    return brut.length > 28 ? `${brut.slice(0, 27).trimEnd()}…` : brut;
}

const cacheEnveloppes = new WeakMap();

/**
 * C5 : une enveloppe par circuit ayant au moins 3 PAV actifs géolocalisés. `concave: true` (C6) tente l'enveloppe concave
 * (maxEdge 1,5 km) et retombe sur la convexe si elle échoue. Mémoïsé par index et par variante.
 */
export function enveloppesCircuits(index, {concave: avecConcave = false} = {}) {
    let parVariante = cacheEnveloppes.get(index);
    if (!parVariante) cacheEnveloppes.set(index, (parVariante = new Map()));
    const cle = avecConcave ? 'concave' : 'convexe';
    if (parVariante.has(cle)) return parVariante.get(cle);

    const points = new Map();
    for (const pav of index.pavs) {
        if (pav.statut === 'RETIRÉ' || pav.lat == null || pav.lng == null) continue;
        let liste = points.get(pav.patrimoineId);
        if (!liste) points.set(pav.patrimoineId, (liste = []));
        liste.push(point([pav.lng, pav.lat]));
    }
    const features = [];
    for (const c of listeCircuits(index)) {
        const pts = points.get(c.id);
        if (c.retire || !pts || pts.length < 3) continue;
        const fc = featureCollection(pts);
        let polygone = null;
        if (avecConcave) {
            try { polygone = concave(fc, {maxEdge: 1.5, units: 'kilometers'}); } catch (e) { polygone = null; }
        }
        if (!polygone) polygone = convex(fc);
        if (!polygone) continue; // points alignés ou confondus
        features.push({
            type: 'Feature',
            geometry: polygone.geometry,
            properties: {patrimoineId: c.id, libelle: c.libelle, groupe: c.groupe, nbPav: pts.length, couleur: c.couleur, couleurClair: c.couleurClair},
        });
    }
    const res = {type: 'FeatureCollection', features};
    parVariante.set(cle, res);
    return res;
}

/** Circuit sous un point : le plus petit polygone qui le contient l'emporte (les circuits se recouvrent). */
export function trouverCircuit(enveloppes, lat, lng) {
    const p = point([lng, lat]);
    let meilleur = null;
    let surface = Infinity;
    for (const f of enveloppes.features) {
        if (!booleanPointInPolygon(p, f)) continue;
        const a = area(f);
        if (a < surface) { meilleur = f; surface = a; }
    }
    return meilleur;
}

/** Position du libellé d'un polygone : [lat, lng]. */
export function centreLibelle(feature) {
    const [lng, lat] = centroid(feature).geometry.coordinates;
    return [lat, lng];
}
